import React, { useCallback, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import { Box, Grid, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Close } from "@mui/icons-material";
import Button from "./Button";
import CustomDatePicker from "./CustomDatePicker";
import dayjs from "dayjs";
import { useForm } from "react-hook-form";
import { useModalStyles } from "./useModalStyles";
import { FieldLabel } from "../../utils/Utility";

export type ValidationProps = {
    type : 'isBefore' | 'isAfter' | 'isSame';
    date : string;
}

interface DatePickerPopupProps {
    title: string;
    label: string;
    open: boolean;
    validationArray?: ValidationProps[];
    submitDate: (date: string)=> void;
    isDisablePast?: boolean;
    isDisableFuture?: boolean;
    onClose: () => void;
    maxDate?: Date;
}

const DatePickerPopup: React.FC<DatePickerPopupProps> = ({
    title,
    label,
    open,
    submitDate,
    validationArray,
    isDisablePast,
    isDisableFuture,
    maxDate,
    onClose
}) => {

    const { control, handleSubmit, reset} = useForm<{date: string}>({ defaultValues: {date: ""} });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {
        tokens,
        paperSx,
        headerSx,
        closeIconSx,
        bodySx,
        footerSx,
        datepickerWrapSx,
        primaryBtnSx,
        cancelBtnSx,
    } = useModalStyles();

    useEffect(()=>{
        if (!open)
            reset()
    },[open]);

    const validateDate = useCallback((value:  string | number | Date | dayjs.Dayjs | null | undefined)=>{
        let date = value? dayjs(value): null;
        if (!date?.isValid())
            return "Invalid Date";
        let errors =  validationArray?.map((el: ValidationProps)=>{
            if (el.type === 'isAfter' && date?.isAfter(dayjs(el.date)))
                return `${label} date should not be a future date to ${el.date}`;
            if (el.type === 'isBefore' && date?.isBefore(dayjs(el.date)))
                return `${label} date should be a future date to ${el.date}`;
            if (el.type === 'isAfter' && date?.isSame(dayjs(el.date)))
                return `${label} date should not be a same date to ${el.date}`;
        })

        return (errors && errors.length > 0)? errors[0]: true;
        

    },[open])

    const onSubmit = useCallback((data: {date: string}) => {
        submitDate(dayjs(data.date).format("MM/DD/YYYY"));
      }, [open]);

    return (
        <>
            <Dialog
                open={open}
                keepMounted
                onClose={onClose}
                aria-describedby="alert-dialog-slide-description"
                onClick={(event) => {
                    event.stopPropagation();
                }}
                maxWidth={"sm"}
                fullWidth
                fullScreen={isMobile}
                PaperProps={{ sx: paperSx(isMobile) }}
            >
                <form
                    onSubmit={(event) => {
                        // Prevent the submit event from bubbling up to a parent form
                        // (this popup can be rendered inside another form via a portal),
                        // which would otherwise trigger the parent's submit/save even when
                        // this date field is invalid.
                        event.preventDefault();
                        event.stopPropagation();
                        handleSubmit(onSubmit)(event);
                    }}
                >
                <Box sx={headerSx}>
                    <Box sx={{ flex: 1, pr: 4 }}>
                        <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                            {title}
                        </Typography>
                        <Typography id="alert-dialog-slide-description" sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                            Select a date to continue.
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
                        <Close sx={{ fontSize: "1rem" }} />
                    </IconButton>
                </Box>
                <Box sx={bodySx}>
                    <Box>
                        <Grid container spacing={2}>
                        <Grid item md={12} xs={12}>
                        <Box>
                            <FieldLabel label={label || "Date"} required tokens={tokens} />
                            <Box sx={datepickerWrapSx(false)}>
                                <CustomDatePicker
                                    label=""
                                    openTo="day"
                                    controllerName="date"
                                    control={control}
                                    format="MM/DD/YYYY"
                                    disableFuture={isDisableFuture}
                                    disablePast={isDisablePast}
                                    maxDate={maxDate}
                                    rules={{
                                      validate: {
                                      isValidDate: (
                                        value:
                                        | string
                                        | number
                                        | Date
                                        | dayjs.Dayjs
                                        | null
                                        | undefined
                                      ) =>
                                      validateDate(value),
                                      },
                                    }}
                                  />
                            </Box>
                        </Box>
                </Grid>
                        </Grid>
                    </Box>
                </Box>
                <Box sx={footerSx}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1.5,
                            flexDirection: { xs: "column", sm: "row" },
                        }}
                    >
                        <Box
                            component="button"
                            type="submit"
                            sx={{
                                ...primaryBtnSx,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 3,
                                py: 1.2,
                                border: "none",
                                cursor: "pointer",
                                color: "#fff",
                                order: { xs: 1, sm: 2 },
                            }}
                        >
                            Submit
                        </Box>
                        <Box
                            component="button"
                            type="button"
                            onClick={onClose}
                            sx={{
                                ...cancelBtnSx,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 3,
                                py: 1.2,
                                border: "1px solid",
                                cursor: "pointer",
                                order: { xs: 2, sm: 1 },
                            }}
                        >
                            Cancel
                        </Box>
                    </Box>
                </Box>
                </form>
            </Dialog>
        </>
    );
};

export default DatePickerPopup;
